import React, {
  AriaAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-hot-toast';
import * as styles from './Toast.module.scss';
import { getFocusedElement } from './createToast';
import { IconType } from 'components/core/Icon/Icon';
import { Button, Icon, IconList } from 'components/core';

const isEditingText = (element: Element | null): boolean =>
  element instanceof HTMLElement &&
  (element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT' ||
    element.isContentEditable);

// @headlessui/react's Dialog (used by our Modal component) marks its root
// element aria-modal="true". Toasts render into a portal outside any such
// dialog, so stealing focus into one would break the dialog's own focus
// trap out from under it.
const isInsideOpenModal = (element: Element | null): boolean =>
  element?.closest('[aria-modal="true"]') != null;

// Moves focus into the toast's first interactive element (or the toast
// itself) as it becomes visible, then returns focus to wherever it came
// from once the toast closes - unless the user has since moved on
// elsewhere, is actively editing text, or is inside an open modal dialog
// (in which case focus is left alone; the toast is still shown and
// announced via its aria-live/role attributes regardless of whether focus
// moves). Only enable this for toasts with actionable content the user
// would otherwise have to hunt for in tab order - see the `focusOnAppear`
// prop doc for why this isn't the default for every toast.
const useToastFocusManagement = ({
  visible,
  focusOnAppear,
  restoreFocusTo,
  containerRef,
}: {
  visible?: boolean;
  focusOnAppear?: boolean;
  restoreFocusTo?: HTMLElement | null;
  containerRef: React.RefObject<HTMLDivElement>;
}) => {
  useEffect(() => {
    if (!visible || !focusOnAppear) return;

    const previouslyFocused = restoreFocusTo ?? getFocusedElement();

    if (
      !isEditingText(document.activeElement) &&
      !isInsideOpenModal(document.activeElement)
    ) {
      const target = containerRef.current?.querySelector<HTMLElement>(
        'button:not([disabled]), a[href]'
      );
      (target || containerRef.current)?.focus();
    }

    return () => {
      // Only take focus back if it's still inside the toast being closed -
      // if the user has already moved on elsewhere, leave it alone.
      if (containerRef.current?.contains(document.activeElement)) {
        previouslyFocused?.focus();
      }
    };
  }, [visible, focusOnAppear, restoreFocusTo, containerRef]);
};

export type ToastType =
  | 'neutral'
  | 'info'
  | 'warning'
  | 'success'
  | 'danger'
  | 'primary'
  | 'brand'
  | undefined;

type Props = {
  children: ReactNode;
  /**
   * Icon reference
   */
  icon?: IconType;
  className?: string;
  type?: ToastType;
  variant?: 'solid' | 'subtle';
  role?: 'status' | 'alert';
  /**
   * Sets the aria-live value to assertive. This will interrupt screen readers
   * and should only be used when the notification is time-critical.
   */
  assertive?: boolean;
  // provided by createToast
  toastId?: string;
  visible?: boolean;
  // Set to false to hide the dismiss button on the toast
  showCloseButton?: boolean;
  /**
   * Moves keyboard focus into this toast as it appears, and returns it when
   * the toast closes. Off by default: per WCAG 2.2 SC 4.1.3 and the
   * WAI-ARIA Alert pattern, status messages should be announced via
   * aria-live/role WITHOUT taking focus - forcing focus on every toast can
   * interrupt an unrelated task the user is in the middle of, or break an
   * open modal dialog's own focus trap. Only turn this on for toasts that
   * carry real actions (e.g. Undo/View/Retry) where reaching them quickly
   * outweighs that risk - a toast that's just an informational message
   * doesn't need it, since the close button (and any actions) are still
   * reachable via normal tab order regardless.
   */
  focusOnAppear?: boolean;
  /**
   * The element to return keyboard focus to once the toast closes. Pass the
   * element that triggered this toast, captured synchronously (e.g. the
   * first line of the onClick handler) before any state updates or async
   * work that might disable or unmount it. By the time the toast actually
   * becomes visible, that element may already have lost focus for unrelated
   * reasons (e.g. a loading spinner disabling the triggering button), so
   * `document.activeElement` at that point can no longer be trusted as the
   * true trigger - pass it explicitly instead.
   */
  restoreFocusTo?: HTMLElement | null;
} & AriaAttributes;

export const Toast = ({
  children,
  icon,
  className,
  type = 'neutral',
  variant = 'solid',
  toastId,
  visible,
  showCloseButton = true,
  focusOnAppear = false,
  restoreFocusTo,
  ...props
}: Props) => {
  if (!toastId)
    throw new Error(
      'No toast reference provided. Was this Toast created using `createToast`?'
    );

  // Trailing visible state so we can add the visible css after render for entry transitions
  const [isVisible, setVisible] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useToastFocusManagement({
    visible,
    focusOnAppear,
    restoreFocusTo,
    containerRef,
  });

  const classes = [styles.Toast, styles[type], styles[variant]];
  if (className) classes.push(className);
  if (isVisible) classes.push(styles.visible);

  useEffect(() => {
    if (isVisible !== visible) setVisible(!!visible);
  }, [visible, isVisible]);

  return (
    <div
      className={classes.join(' ')}
      ref={containerRef}
      tabIndex={focusOnAppear ? -1 : undefined}
      {...props}
    >
      {icon ? (
        <div className={styles.IconContainer}>
          <Icon icon={icon} />
        </div>
      ) : null}
      {children}
      {showCloseButton && (
        <Button
          className={`${styles.ToastButton}`}
          iconOnly
          isNaked
          onClick={() => toast.dismiss(toastId)}
          aria-label="Close toast"
          htmlType="button"
        >
          <Icon icon={IconList.light.faClose} className={styles.closeIcon} />
        </Button>
      )}
    </div>
  );
};
